// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useId, useRef, useState, type KeyboardEvent } from 'react';

import { useCanEdit } from '../../app/CaseRoleContext';
import { formatYaw, parseNumberDa } from '../../bygning/panoramaPlacement';
import { Button } from '../Button';
import { IconButton, XIcon } from '../IconButton';
import { TextInput } from '../TextInput';
import styles from './PlacementPanel.module.css';

/** A field the panel commits: heading (°), world x / y (m), height over the storey floor (m). */
export type PlacementField = 'yaw' | 'x' | 'y' | 'height';

/** What the panel shows: nothing picked, a picked panorama, or a new one being added. */
export type PlacementView =
  | { kind: 'idle' }
  | {
      kind: 'selected';
      id: number;
      filename: string;
      /** "Placeret manuelt", "Justeret · 412 punkter", … */
      source: string;
      /** World position, metres. */
      x: number;
      y: number;
      yaw: number | null;
      /** Over the storey floor (or world z without storeys, `heightLabel` says which). */
      height: number;
      heightLabel: string;
      moving: boolean;
      saving: boolean;
    }
  | {
      kind: 'add';
      filename: string;
      /** "8192 × 4096 px · 12,4 MB" */
      detail: string;
      placed: boolean;
      height: number;
      heightLabel: string;
      yaw: number;
      /** 0..1 while uploading, else null. */
      progress: number | null;
    };

export interface PlacementPanelProps {
  view: PlacementView;
  error: string | null;
  /** A JPEG was picked for Tilføj 360°-billede. */
  onPickFile: (file: File) => void;
  onToggleMove: () => void;
  onOpen: (id: number) => void;
  /** A typed value, committed on Enter or leaving the field. */
  onCommit: (field: PlacementField, value: number) => void;
  onSubmitAdd: () => void;
  /** Stop an upload in flight. */
  onCancelUpload: () => void;
  onCancelAdd: () => void;
  onClose: () => void;
}

/** A number field in Danish notation that commits on Enter / blur and resets on Esc. */
function NumberField({
  label,
  value,
  unit,
  disabled,
  onCommit,
  format,
}: {
  label: string;
  value: number | null;
  unit: string;
  disabled?: boolean;
  onCommit: (value: number) => void;
  format: (v: number) => string;
}) {
  const id = useId();
  const shown = value === null ? '' : format(value);
  const [draft, setDraft] = useState(shown);
  const [invalid, setInvalid] = useState(false);
  // A new value from outside (a drag, the server) replaces the draft.
  const seen = useRef(shown);
  if (seen.current !== shown) {
    seen.current = shown;
    setDraft(shown);
    setInvalid(false);
  }
  const commit = () => {
    if (draft === shown) return;
    const n = parseNumberDa(draft);
    if (n === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onCommit(n);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    } else if (event.key === 'Escape') {
      // Reset the field, not the whole tool.
      event.stopPropagation();
      setDraft(shown);
      setInvalid(false);
    }
  };
  return (
    <label className={styles.field} htmlFor={id}>
      <span className={styles.fieldLabel}>{label}</span>
      <TextInput
        id={id}
        size="sm"
        mono
        inputMode="decimal"
        value={draft}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        suffix={unit}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
      />
    </label>
  );
}

const formatZ = (v: number) => v.toLocaleString('da-DK', { maximumFractionDigits: 2, useGrouping: false });
const formatXy = (v: number) => v.toLocaleString('da-DK', { maximumFractionDigits: 3, useGrouping: false });

/**
 * Placér 360°: the locked plan's panel for placing panoramas by hand
 * (frontend-ayw.3) — the picked marker's Flyt, heading and height, or a new
 * JPEG being added: where it goes, its height and heading, the upload.
 * Editors only; the Ground decides when it shows.
 */
export function PlacementPanel({
  view,
  error,
  onPickFile,
  onToggleMove,
  onOpen,
  onCommit,
  onSubmitAdd,
  onCancelUpload,
  onCancelAdd,
  onClose,
}: PlacementPanelProps) {
  const canEdit = useCanEdit();
  const fileRef = useRef<HTMLInputElement>(null);
  const uploading = view.kind === 'add' && view.progress !== null;
  // Every control here writes: a viewer never sees the panel.
  if (!canEdit) return null;

  return (
    <section className={styles.panel} aria-label="Placér 360°-billeder">
      <header className={styles.head}>
        <h2 className={styles.title}>{view.kind === 'add' ? 'Tilføj 360°-billede' : '360°-billeder'}</h2>
        <IconButton variant="ghost" label="Luk placering af 360°-billeder" onClick={onClose}>
          <XIcon />
        </IconButton>
      </header>
      <div className={styles.body}>
        {view.kind === 'idle' && (
          <>
            <p className={styles.note}>Klik på en markør i planen for at flytte den eller dreje retningen.</p>
            <Button variant="outline" size="sm" block onClick={() => fileRef.current?.click()}>
              Tilføj 360°-billede…
            </Button>
          </>
        )}

        {view.kind === 'selected' && (
          <>
            <div className={styles.ident}>
              <span className={styles.file} title={view.filename}>
                {view.filename}
              </span>
              <span className={styles.source}>{view.source}</span>
            </div>
            <div className={styles.actions}>
              <Button
                variant={view.moving ? 'primary' : 'outline'}
                size="sm"
                aria-pressed={view.moving}
                onClick={onToggleMove}
                disabled={view.saving}
              >
                {view.moving ? 'Færdig' : 'Flyt'}
              </Button>
              <Button variant="filled" size="sm" onClick={() => onOpen(view.id)}>
                Åbn
              </Button>
            </div>
            {view.moving && (
              <p className={styles.note}>
                Træk markøren for at flytte den, pilen for at dreje (Shift: 15°). Piletasterne flytter 0,1 m (Shift: 1 m);
                Esc fortryder.
              </p>
            )}
            <div className={styles.fields}>
              <NumberField label="X" unit="m" value={view.x} format={formatXy} onCommit={(v) => onCommit('x', v)} />
              <NumberField label="Y" unit="m" value={view.y} format={formatXy} onCommit={(v) => onCommit('y', v)} />
              <NumberField label="Retning" unit="°" value={view.yaw} format={formatYaw} onCommit={(v) => onCommit('yaw', v)} />
              <NumberField
                label={view.heightLabel}
                unit="m"
                value={view.height}
                format={formatZ}
                onCommit={(v) => onCommit('height', v)}
              />
            </div>
            {view.saving && (
              <p className={styles.note} role="status">
                Gemmer placeringen…
              </p>
            )}
          </>
        )}

        {view.kind === 'add' && (
          <>
            <div className={styles.ident}>
              <span className={styles.file} title={view.filename}>
                {view.filename}
              </span>
              <span className={styles.source}>{view.detail}</span>
            </div>
            <p className={styles.note}>
              {view.placed
                ? 'Klik et andet sted i planen for at flytte billedet.'
                : 'Klik i planen, hvor billedet er taget.'}
            </p>
            <div className={styles.fields}>
              <NumberField
                label="Retning"
                unit="°"
                value={view.yaw}
                disabled={uploading}
                format={formatYaw}
                onCommit={(v) => onCommit('yaw', v)}
              />
              <NumberField
                label={view.heightLabel}
                unit="m"
                value={view.height}
                disabled={uploading}
                format={formatZ}
                onCommit={(v) => onCommit('height', v)}
              />
            </div>
            {uploading && (
              <div className={styles.progress} role="progressbar" aria-label="Uploader" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((view.progress ?? 0) * 100)}>
                <div className={styles.progressBar} style={{ transform: `scaleX(${view.progress ?? 0})` }} />
              </div>
            )}
            <div className={styles.actions}>
              <Button variant="primary" size="sm" onClick={onSubmitAdd} disabled={!view.placed || uploading} aria-busy={uploading || undefined}>
                {uploading
                  ? (view.progress ?? 0) >= 1
                    ? 'Serveren gemmer…'
                    : `Uploader… ${Math.round((view.progress ?? 0) * 100)} %`
                  : 'Tilføj billedet'}
              </Button>
              {uploading ? (
                <Button variant="filled" size="sm" onClick={onCancelUpload}>
                  Annullér upload
                </Button>
              ) : (
                <Button variant="filled" size="sm" onClick={onCancelAdd}>
                  Annullér
                </Button>
              )}
            </div>
          </>
        )}

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/pjpeg,.jpg,.jpeg"
          className={styles.fileInput}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) onPickFile(file);
          }}
        />
      </div>
    </section>
  );
}
